import React, { useState, useEffect } from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import classes from "./styles.module.css";

/**
 * TerminalFeesTab — вкладка «Комиссии» в админке терминалов.
 *
 * Props:
 *   token (string) — JWT-токен
 *   terminal (object) — { id, projectUuid, ... }
 */
function TerminalFeesTab(props) {
    var token = props.token;
    var terminal = props.terminal;

    // Состояние комиссий
    var _payment = useState({ cost: "", fixedPerTransaction: "", costFixedMinimum: "" });
    var payment = _payment[0];
    var setPayment = _payment[1];

    var _payout = useState({ cost: "", fixedPerTransaction: "", costFixedMinimum: "" });
    var payout = _payout[0];
    var setPayout = _payout[1];

    var _refund = useState({ cost: "", fixedPerTransaction: "", costFixedMinimum: "" });
    var refund = _refund[0];
    var setRefund = _refund[1];

    var _saving = useState(false);
    var saving = _saving[0];
    var setSaving = _saving[1];

    var _loading = useState(true);
    var loading = _loading[0];
    var setLoading = _loading[1];

    var _status = useState(null); // {type: 'success'|'error', text: '...'}
    var status = _status[0];
    var setStatus = _status[1];

    // Загрузка комиссий
    useEffect(function () {
        if (!terminal || !terminal.id) return;
        setLoading(true);
        setStatus(null);

        var api = new Api({ token: token });
        api.getAdminTerminalFees(terminal.id).then(function (data) {
            var fees = data.fees || {};
            setPayment({
                cost: String(fees.payment && fees.payment.cost != null ? fees.payment.cost : ""),
                fixedPerTransaction: String(fees.payment && fees.payment.fixedPerTransaction != null ? fees.payment.fixedPerTransaction : ""),
                costFixedMinimum: String(fees.payment && fees.payment.costFixedMinimum != null ? fees.payment.costFixedMinimum : ""),
            });
            setPayout({
                cost: String(fees.payout && fees.payout.cost != null ? fees.payout.cost : ""),
                fixedPerTransaction: String(fees.payout && fees.payout.fixedPerTransaction != null ? fees.payout.fixedPerTransaction : ""),
                costFixedMinimum: String(fees.payout && fees.payout.costFixedMinimum != null ? fees.payout.costFixedMinimum : ""),
            });
            setRefund({
                cost: String(fees.refund && fees.refund.cost != null ? fees.refund.cost : ""),
                fixedPerTransaction: String(fees.refund && fees.refund.fixedPerTransaction != null ? fees.refund.fixedPerTransaction : ""),
                costFixedMinimum: String(fees.refund && fees.refund.costFixedMinimum != null ? fees.refund.costFixedMinimum : ""),
            });
            setLoading(false);
        }).catch(function (err) {
            Sentry.captureException(err);
            setLoading(false);
            setStatus({ type: "error", text: "Ошибка загрузки комиссий" });
        });
    }, [terminal && terminal.id, token]);

    function onFieldChange(group, field, value) {
        // Разрешаем только числа с точкой
        if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;

        if (group === "payment") {
            var next = {};
            for (var k in payment) { next[k] = payment[k]; }
            next[field] = value;
            setPayment(next);
        } else if (group === "payout") {
            var next2 = {};
            for (var k2 in payout) { next2[k2] = payout[k2]; }
            next2[field] = value;
            setPayout(next2);
        } else if (group === "refund") {
            var next3 = {};
            for (var k3 in refund) { next3[k3] = refund[k3]; }
            next3[field] = value;
            setRefund(next3);
        }
    }

    function onSave() {
        setSaving(true);
        setStatus(null);

        var fees = {
            payment: {
                cost: payment.cost !== "" ? parseFloat(payment.cost) : 0,
                fixedPerTransaction: payment.fixedPerTransaction !== "" ? parseFloat(payment.fixedPerTransaction) : 0,
                costFixedMinimum: payment.costFixedMinimum !== "" ? parseFloat(payment.costFixedMinimum) : 0,
            },
            payout: {
                cost: payout.cost !== "" ? parseFloat(payout.cost) : 0,
                fixedPerTransaction: payout.fixedPerTransaction !== "" ? parseFloat(payout.fixedPerTransaction) : 0,
                costFixedMinimum: payout.costFixedMinimum !== "" ? parseFloat(payout.costFixedMinimum) : 0,
            },
            refund: {
                cost: refund.cost !== "" ? parseFloat(refund.cost) : 0,
                fixedPerTransaction: refund.fixedPerTransaction !== "" ? parseFloat(refund.fixedPerTransaction) : 0,
                costFixedMinimum: refund.costFixedMinimum !== "" ? parseFloat(refund.costFixedMinimum) : 0,
            },
        };

        var api = new Api({ token: token });
        api.updateAdminTerminalFees(terminal.id, fees).then(function () {
            setSaving(false);
            setStatus({ type: "success", text: "Комиссии сохранены" });
        }).catch(function (err) {
            Sentry.captureException(err);
            setSaving(false);
            var msg = (err && err.response && err.response.data && err.response.data.message) || "Ошибка сохранения";
            setStatus({ type: "error", text: msg });
        });
    }

    function renderFeeGroup(title, description, operations, group, state) {
        return React.createElement("div", { className: classes.FeeGroup },
            React.createElement("div", { className: classes.FeeGroupTitle }, title),
            React.createElement("div", { className: classes.FeeGroupDescription }, operations),
            React.createElement("div", { className: classes.FieldRow },
                React.createElement("div", { className: classes.Field },
                    React.createElement("div", { className: classes.FieldLabel }, "Процент (%)"),
                    React.createElement("input", {
                        className: classes.FieldInput,
                        type: "text",
                        value: state.cost,
                        onChange: function (e) { onFieldChange(group, "cost", e.target.value); },
                        placeholder: "0",
                    }),
                    React.createElement("div", { className: classes.FieldHint }, "Процент от суммы транзакции")
                ),
                React.createElement("div", { className: classes.Field },
                    React.createElement("div", { className: classes.FieldLabel }, "Фикс. за транзакцию (руб)"),
                    React.createElement("input", {
                        className: classes.FieldInput,
                        type: "text",
                        value: state.fixedPerTransaction,
                        onChange: function (e) { onFieldChange(group, "fixedPerTransaction", e.target.value); },
                        placeholder: "0",
                    }),
                    React.createElement("div", { className: classes.FieldHint }, "Фиксированная сумма за каждую транзакцию")
                ),
                React.createElement("div", { className: classes.Field },
                    React.createElement("div", { className: classes.FieldLabel }, "Мин. комиссия (руб)"),
                    React.createElement("input", {
                        className: classes.FieldInput,
                        type: "text",
                        value: state.costFixedMinimum,
                        onChange: function (e) { onFieldChange(group, "costFixedMinimum", e.target.value); },
                        placeholder: "0",
                    }),
                    React.createElement("div", { className: classes.FieldHint }, "Минимальная сумма процентной комиссии")
                )
            )
        );
    }

    if (loading) {
        return React.createElement("div", { className: classes.Container }, "Загрузка комиссий...");
    }

    return React.createElement("div", { className: classes.Container },
        React.createElement("div", { className: classes.Title }, "Комиссии терминала"),

        renderFeeGroup(
            "Оплата (Payment)",
            "Применяется к входящим платежам",
            "Purchase, Recurrent, SBP, M_P2P, P2P, QR",
            "payment",
            payment
        ),
        renderFeeGroup(
            "Выплаты (Payout)",
            "Применяется к выплатам",
            "Payout, Mobile Payout",
            "payout",
            payout
        ),
        renderFeeGroup(
            "Возвраты (Refund)",
            "Применяется к возвратам",
            "Refund",
            "refund",
            refund
        ),

        React.createElement("div", { style: { marginTop: "15px", padding: "10px", background: "#f5f5f5", borderRadius: "4px", fontSize: "12px", color: "#666" } },
            "Формула: fixedPerTransaction + max(amount \u00D7 cost%, costFixedMinimum). Фиксированная сумма и мин. комиссия указываются в рублях."
        ),

        React.createElement("button", {
            className: classes.SaveBtn,
            onClick: onSave,
            disabled: saving,
        }, saving ? "Сохранение..." : "Сохранить комиссии"),

        status && React.createElement("div", {
            className: classes.StatusMsg + " " + (status.type === "success" ? classes.StatusSuccess : classes.StatusError),
        }, status.text)
    );
}

export default TerminalFeesTab;



// WEBPACK FOOTER //
// ./src/Components/TerminalFeesTab/index.js